import type { JournalEntry } from "@journal/powersync";
import {
  buildDataset,
  type ExportColumn,
  type ExportDataset,
  type ExportMetadata,
} from "@fleet/export";

/**
 * Journal adapter: maps a list of `journal_entry` records into the generic
 * {@link ExportDataset}. This is the ONLY entry-specific piece — the canon todo
 * adapter is swapped for this one per the K1 propagation notes.
 *
 * The mood is stored as a 1–5 integer (see `MOOD_VALUES`); we surface a
 * human-readable label so the CSV/PDF is legible without the app. The body is
 * authored as plain text in the composer, so it passes through untouched (the
 * CSV/HTML serializers already escape newlines/commas/markup).
 */

/** Readable labels for the stored 1–5 mood scale (blank for missing/out-of-range). */
const MOOD_EXPORT_LABELS: Record<number, string> = {
  1: "Very low",
  2: "Low",
  3: "Neutral",
  4: "Good",
  5: "Great",
};

/** Maps the stored mood integer to a readable label; empty when unset/invalid. */
export function moodLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  return MOOD_EXPORT_LABELS[value] ?? "";
}

export const ENTRY_EXPORT_COLUMNS: ExportColumn<JournalEntry>[] = [
  { key: "entry_date", header: "Date", accessor: (e) => e.entry_date ?? "" },
  { key: "mood", header: "Mood", accessor: (e) => moodLabel(e.mood) },
  { key: "title", header: "Title", accessor: (e) => e.title ?? "" },
  { key: "body", header: "Entry", accessor: (e) => e.body ?? "" },
  { key: "created_at", header: "Created", accessor: (e) => e.created_at ?? "" },
];

/**
 * Builds an {@link ExportDataset} from a list of journal entries, ready for
 * `datasetToCsv` / `datasetToHtml`.
 */
export function buildEntryDataset(
  entries: readonly JournalEntry[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(ENTRY_EXPORT_COLUMNS, entries, {
    title: "Journal entries",
    ...metadata,
  });
}
